import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LeafletMap } from "@/components/LeafletMap";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtTime } from "@/lib/format";
import { Loader2, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function range(preset) {
  const today = new Date();
  const d = (n) => new Date(today.getTime() - n * 86400000).toISOString().slice(0, 10);
  if (preset === "today") return { date_from: d(0), date_to: d(0) };
  if (preset === "yesterday") return { date_from: d(1), date_to: d(1) };
  if (preset === "week") return { date_from: d(7), date_to: d(0) };
  return {};
}

export default function MapView() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("all");
  const [driverId, setDriverId] = useState("all");
  const [status, setStatus] = useState("all");

  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });
  const { data: points = [], isLoading } = useQuery({
    queryKey: ["map", preset, driverId, status],
    queryFn: async () => (await api.get("/map/deliveries", { params: {
      ...range(preset), driver_id: driverId === "all" ? undefined : driverId, status: status === "all" ? undefined : status,
    } })).data,
    refetchInterval: 20000,
  });

  const mapPoints = points.map((p) => ({
    lat: p.lat, lng: p.lng, status: p.status,
    label: `<b>#${p.order_number}</b><br/>${p.customer}<br/>${p.driver || ""} · ${p.status}`,
  }));

  return (
    <div className="animate-slide-up">
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Live Delivery Map</h1>
      <p className="text-sm text-slate-500">{points.length} delivery locations</p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Select value={preset} onValueChange={setPreset}><SelectTrigger className="w-[150px] bg-white" data-testid="map-date-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All time</SelectItem><SelectItem value="today">Today</SelectItem><SelectItem value="yesterday">Yesterday</SelectItem><SelectItem value="week">This week</SelectItem></SelectContent>
        </Select>
        <Select value={driverId} onValueChange={setDriverId}><SelectTrigger className="w-[150px] bg-white" data-testid="map-driver-filter"><SelectValue placeholder="Driver" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All drivers</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[160px] bg-white" data-testid="map-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{["Completed","In Transit","Arrived","Assigned"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {isLoading ? <div className="flex h-[500px] items-center justify-center rounded-xl border border-slate-200 bg-white"><Loader2 className="animate-spin text-[#FF6500]" /></div>
            : <LeafletMap height={500} points={mapPoints} className="border border-slate-200" />}
        </div>
        <div className="max-h-[500px] space-y-2 overflow-y-auto scrollbar-thin">
          {points.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`map-card-${p.order_number}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-[#FF6500]">#{p.order_number}</span>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-1 font-semibold text-[#0B192C]">{p.customer}</div>
              <div className="text-xs text-slate-500">{p.driver || "—"} · Arrived {fmtTime(p.arrived_at)}</div>
              <div className="mt-0.5 font-mono text-xs text-slate-400">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</div>
              {p.pod_number && <button onClick={() => navigate(`/admin/pod/${p.id}`)} className="mt-1 flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><FileText size={12} /> {p.pod_number}</button>}
            </div>
          ))}
          {points.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">No locations</div>}
        </div>
      </div>
    </div>
  );
}
